<template>
  <section class="content">
    <div id="overlay" v-if="loading">
      <div class="spinner"></div>
      <br />
      <h4>Loading...</h4>
    </div>
    <div>
      <div class="box box-info">
        <form>
          <div class="p-fluid p-formgrid p-grid">
            <!-- Date Fields -->
            <div class="p-field p-col-12 p-md-2">
              <label for="fromDate">From Date</label>
              <date-picker v-model="fromDate" valueType="format" v-if="!englishDateEnabled" />
              <InputMask mask="9999-99-99" type="text" v-else class="nepali-calendar" id="fromDate" v-model="fromDate" />
            </div>

            <div class="p-field p-col-12 p-md-2">
              <label for="toDate">To Date</label>
              <date-picker v-model="toDate" valueType="format" v-if="!englishDateEnabled" />
              <input-mask mask="9999-99-99" type="text" v-else class="nepali-calendar" id="toDate" v-model="toDate" />
            </div>

            <!-- Item Selection -->
            <div class="p-field p-col-12 p-md-3">
              <label for="itemGroups">Item Category</label>
              <Dropdown
                v-model="selectedGroup"
                :options="itemGroups"
                optionLabel="name"
                placeholder="Select Category"
                class="dropdown-custom"
                @change="filterItemsByGroup"
              />
            </div>
            
            <div class="p-field p-col-12 p-md-3">
              <label for="items">Items</label>
              <MultiSelect 
                v-model="selectedItems" 
                :options="filteredItems"
                optionLabel="name" 
                placeholder="Select Items"
                :showCheckbox="true" 
                :filter="true"
                @filter="searchItems"
                class="multiselect-custom"
              >
                <template #option="slotProps">
                  <div class="p-multiselect-item-option">
                    <span>{{ slotProps.option.name }}</span>
                  </div>
                </template>
              </MultiSelect>
            </div>

            <div class="p-field p-col-12 p-md-2 p-mt-5">
              <Button @click.prevent="searchItemMargin" class="p-button-raised" label="Search" icon="pi pi-search" />
            </div>
          </div>
        </form>

        <!-- Report Table -->
        <div class="p-fluid p-formgrid p-grid">
          <div class="p-field p-col-12 p-col-12">
            <a href="#" class="p-field p-col-12 p-md-12" @click.prevent="exportCSV()"
              v-if="itemMarginData.length > 0">
              <img src="../../../../static/img/excel.png" style="float:right;height: 3em;width: 3em;" />
            </a>
            
            <DataTable :value="itemMarginData" :paginator="true" :rows="100" 
                       :filters="filters" :rowsPerPageOptions="[100, 500, 1000]" 
                       class="p-datatable-responsive p-datatable-gridlines" 
                       data-key="id">
              <Column header="S.N">
                <template #body="sl">
                  {{ sl.index + 1 }}
                </template>
              </Column>
              <Column header="Sales Date" :sortable="true" field="salesDateEnglish" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['salesDateEnglish']" />
                </template>
              </Column>
              <Column header="Bill No" :sortable="true" field="billNo" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['billNo']" />
                </template>
              </Column>
              <Column header="Customer" :sortable="true" field="accountName" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['accountName']" />
                </template>
              </Column>
              <Column header="Item Name" :sortable="true" field="itemName" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['itemName']" />
                </template>
              </Column>
              <Column header="Item Code" :sortable="true" field="itemCode" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['itemCode']" />
                </template>
              </Column>
              <Column header="Purchase Rate" bodyStyle="text-align: right" :sortable="true" field="purchaseRate" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.purchaseRate.toFixed(2) }}
                </template>
              </Column>
              <Column header="Sales Rate" bodyStyle="text-align: right" :sortable="true" field="salesRate" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.salesRate.toFixed(2) }}
                </template>
              </Column>
              <Column header="Margin" bodyStyle="text-align: right" :sortable="true" field="margin" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.margin.toFixed(2) }}
                </template>
              </Column>
              <Column header="Margin %" bodyStyle="text-align: right" :sortable="true" field="marginPercent" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['marginPercent']" />
                </template>
                <template #body="slotProps">
                  {{ slotProps.data.marginPercent.toFixed(2) }}%
                </template>
              </Column>
              <Column header="Profit %" bodyStyle="text-align: right" :sortable="true" field="grossMargin" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.grossMargin.toFixed(2) }}%
                </template>
                <!-- <template #footer>
                  {{ totalGrossMargin.toFixed(2) }}
                </template> -->
              </Column>
              <Column header="Vat To Pay" bodyStyle="text-align: right" :sortable="true" field="vatTopay" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.vatTopay.toFixed(2) }}
                </template>
                <template #footer>
                  {{ totalVatToPay.toFixed(2) }}
                </template>
              </Column>
              <Column header="Commission" bodyStyle="text-align: right" :sortable="true" field="commissionAmount" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.commissionAmount.toFixed(2) }}
                </template>
                <template #footer>
                  {{ totalCommissionAmount.toFixed(2) }}
                </template>
              </Column>   
              <Column header="Total Profit" bodyStyle="text-align: right" :sortable="true" field="totalMargin" filterMatchMode="contains">
                <template #body="slotProps">
                  {{ slotProps.data.totalMargin.toFixed(2) }}
                </template>
                <template #footer>
                  {{ totalMarginSum.toFixed(2) }}
                </template>
              </Column>           
              <Column header="Org Name" :sortable="true" field="orgName" filterMatchMode="contains">
                <template #filter>
                  <InputText type="text" class="p-column-filter" v-model="filters['orgName']" />
                </template>
              </Column>
              <template #empty>No record found</template>
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import InputText from 'primevue/inputtext'
import Button from 'primevue/button'
import InputMask from 'primevue/inputmask'
import MultiSelect from 'primevue/multiselect'
import Dropdown from 'primevue/dropdown'
import globalKey from '../../../store/globalKey'
import axios from 'axios'
import $ from 'jquery'

export default {
  name: 'ItemMarginReport',
  data() {
    return {
      fromDate: null,
      toDate: null,
      loading: false,
      filters: {},
      englishDateEnabled: true,
      selectedItems: [],
      selectedGroup: null,
      filteredItems: [],
      itemGroups: [],
      allItems: [],
      itemMarginData: [],
      totalMarginSum: 0,
      totalCommissionAmount: 0,
      totalVatToPay: 0,
      totalGrossMargin: 0
    }
  },
  computed: {
    itemMasterList() {
      return this.$store.state.itemMaster.itemMasterLists
    }
  },
  async mounted() {
    this.checkEnglish()
    if (!this.$store.getters.englishDate || this.$store.state.isEnglishDateEnabled === 'false') {
      this.nepaliDate()
    }
    if (!this.$store.getters.englishDate && this.salesChallan === undefined ||
      this.$store.state.isEnglishDateEnabled === 'false') {
      this.setNepaliDate()
    }
    if (this.$store.state.isEnglishDateEnabled && this.$store.state.isEnglishDateEnabled !== 'false') {
      this.setEnglishDate()
    }
    // Initialize items
    await this.$store.dispatch('loadItemMasterList')
    this.allItems = [...this.itemMasterList]
    const groups = new Set()
    this.allItems.forEach(item => {
      if (item.groupName) {
        groups.add(item.groupName)
      }
    })
    this.itemGroups = Array.from(groups).map(group => ({
      name: group,
      id: group.toLowerCase().replace(/\s+/g, '-')
    }))
    this.itemGroups.unshift({ name: 'All Items', id: 'all-items' })
    this.selectedGroup = this.itemGroups[0]
    this.filteredItems = this.allItems
  },
  methods: {
    filterItemsByGroup() {
      if (!this.selectedGroup || this.selectedGroup.id === 'all-items') {
        this.filteredItems = this.allItems
      } else {
        this.filteredItems = this.allItems.filter(
          item => item.groupName === this.selectedGroup.name
        )
      }
      this.selectedItems = []
    },
    searchItems(event) {
      const filterText = event.filter.toLowerCase() || ''
      if (this.selectedGroup && this.selectedGroup.id !== 'all-items') {
        this.filteredItems = this.allItems.filter(item =>
          item.groupName === this.selectedGroup.name &&
          item.name.toLowerCase().includes(filterText)
        )
      } else {
        this.filteredItems = this.allItems.filter(item =>
          item.name.toLowerCase().includes(filterText)
        )
      }
    },
    async searchItemMargin() {
      this.loading = true
      try {
        const itemIds = this.selectedItems.length > 0
          ? this.selectedItems.map(item => item.id)
          : []
        const payload = {
          fromDate: this.fromDate,
          toDate: this.toDate,
          itemIds: itemIds
        }
        const response = await this.$store.dispatch('loadItemMarginReport', payload)
        console.log(response)
        this.itemMarginData = response.data.data || []
        // Calculate total margin
        this.totalMarginSum = this.itemMarginData.reduce((sum, item) => {
          return sum + (item.totalMargin || 0)
        }, 0)
        this.totalVatToPay = this.itemMarginData.reduce((sum, item) => {
          return sum + (item.vatTopay || 0)
        }, 0)
        this.totalGrossMargin = this.itemMarginData.reduce((sum, item) => {
          return sum + (item.grossMargin || 0)
        }, 0)
        this.totalCommissionAmount = this.itemMarginData.reduce((sum, item) => {
          return sum + (item.commissionAmount || 0)
        }, 0)
        if (this.itemMarginData.length === 0) {
          this.$toast.add({
            severity: 'info',
            summary: '',
            detail: 'No data found for the selected criteria',
            life: 3000
          })
        }
      } catch (error) {
        console.error('Error loading item margin report:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load item margin data',
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },
    exportCSV() {
      const data = {
        fromDate: this.fromDate,
        toDate: this.toDate,
        itemIds: this.selectedItems.length > 0
          ? this.selectedItems.map(item => item.id)
          : null
      }
      axios({
        url: globalKey + '/fileDownload/excelItemMarginReport/' +
             this.$store.getters.organisationId + '/' +
             this.$store.getters.userId + '/' +
             data.fromDate + '/' +
             data.toDate + '/' +
             (data.itemIds ? data.itemIds.join(',') : 'all'),
        method: 'get',
        headers: { 'Accept': 'application/vnd.ms-excel', 'Authorization': localStorage.token },
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'item_margin_report.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      })
    },
    // Date handling methods (same as first code snippet)
    setEnglishDate() {
      this.fromDate = new Date().toISOString().slice(0, 10)
      this.toDate = new Date().toISOString().slice(0, 10)
    },
    checkEnglish() {
      if (this.$store.state.isEnglishDateEnabled === false ||
          this.$store.state.isEnglishDateEnabled === 'false') {
        this.englishDateEnabled = true
      } else {
        this.englishDateEnabled = false
      }
    },
    reset() {
      if (!this.$store.getters.englishDate ||
          this.$store.state.isEnglishDateEnabled === 'false') {
        this.setNepaliDate()
      } else {
        this.setEnglishDate()
      }
    },
    setNepaliDate() {
      if (this.$store.getters.nepaliNewDate !== '') {
        this.fromDate = this.$store.getters.nepaliNewDate
        this.toDate = this.$store.getters.nepaliNewDate
      }
    },
    nepaliDate() {
      $('#fromDate').nepaliDatePicker({
        ndpYear: true,
        ndpMonth: true,
        ndpYearCount: 10,
        disableBefore: this.$store.state.startDate,
        disableAfter: this.$store.state.endDate,
        onChange: () => {
          this.fromDate = $('#fromDate').val()
        }
      })
      $('#toDate').nepaliDatePicker({
        ndpYear: true,
        ndpMonth: true,
        ndpYearCount: 10,
        disableBefore: this.$store.state.startDate,
        disableAfter: this.$store.state.endDate,
        onChange: () => {
          this.toDate = $('#toDate').val()
        }
      })
    }
  },
  components: {
    DatePicker,
    DataTable,
    Column,
    InputText,
    Button,
    InputMask,
    MultiSelect,
    Dropdown
  }
}
</script>

<style scoped>
.multiselect-custom, .dropdown-custom {
  font-size: 1.4rem;
  min-height: 35px;
}

.item-name {
  font-size: 1.3rem;
  margin-left: 8px;
}

::v-deep .p-multiselect-label, 
::v-deep .p-dropdown-label {
  font-size: 1.3rem !important;
  padding: 0.75rem !important;
}

::v-deep .p-multiselect-item,
::v-deep .p-dropdown-item {
  font-size: 1.3rem !important;
  padding: 0.75rem 1.25rem !important;
}
h4 {
  color: #444444;
  margin: 1.5rem 0;
  font-size: 1.8rem;
  border-bottom: 2px solid #dddddd;
  padding-bottom: 0.5rem;
}
</style>



// WEBPACK FOOTER //
// profitReportGross.vue?7978bbb0